/**
 * Profile Constants - Centralized allowed values for profile fields
 * These must match the backend PEARLI spec in pearli_filters.py
 */

// Gender options - Maps user-facing labels to database values
export const GENDER_OPTIONS = [
  { label: "Male", value: "male" },
  { label: "Female", value: "female" },
  { label: "Non-Binary", value: "non_binary" },
  { label: "Other", value: "other" },
  { label: "Prefer Not to Say", value: "prefer_not_to_say" },
];

// Age range options - Maps user-facing labels to database values
export const AGE_OPTIONS = [
  { label: "Under 18", value: "under_18" },
  { label: "18 - 24", value: "18_24" },
  { label: "25 - 29", value: "25_29" },
  { label: "30 - 34", value: "30_34" },
  { label: "35 - 44", value: "35_44" },
  { label: "45 - 55", value: "45_55" },
  { label: "55+", value: "55_plus" },
  { label: "Prefer Not to Say", value: "prefer_not_to_say" },
];

// Country options - Stored in location column
// Primary countries (USA, Canada) at top, followed by other countries
export const COUNTRY_OPTIONS = [
  // Primary/Default Countries
  { label: "United States", value: "US", code: "US", group: "primary" },
  { label: "Canada", value: "CA", code: "CA", group: "primary" },
  { label: "Sri Lanka", value: "LK", code: "LK", group: "primary" },
  
  // Other Countries
  { label: "Mexico", value: "MX", code: "MX", group: "other" },
  { label: "United Kingdom", value: "GB", code: "GB", group: "other" },
  { label: "Australia", value: "AU", code: "AU", group: "other" },
  { label: "New Zealand", value: "NZ", code: "NZ", group: "other" },
  { label: "India", value: "IN", code: "IN", group: "other" },
  { label: "Singapore", value: "SG", code: "SG", group: "other" },
  { label: "Japan", value: "JP", code: "JP", group: "other" },
  { label: "Germany", value: "DE", code: "DE", group: "other" },
  { label: "France", value: "FR", code: "FR", group: "other" },
  { label: "Spain", value: "ES", code: "ES", group: "other" },
  { label: "Italy", value: "IT", code: "IT", group: "other" },
  { label: "Netherlands", value: "NL", code: "NL", group: "other" },
  { label: "Brazil", value: "BR", code: "BR", group: "other" },
  { label: "Argentina", value: "AR", code: "AR", group: "other" },
  { label: "South Africa", value: "ZA", code: "ZA", group: "other" },
  { label: "Sweden", value: "SE", code: "SE", group: "other" },
  { label: "Switzerland", value: "CH", code: "CH", group: "other" },
  { label: "Belgium", value: "BE", code: "BE", group: "other" },
  { label: "South Korea", value: "KR", code: "KR", group: "other" },
  { label: "Ireland", value: "IE", code: "IE", group: "other" },
];

// Validation sets for backend verification
export const VALID_GENDERS = new Set(GENDER_OPTIONS.map((o) => o.value));
export const VALID_AGES = new Set(AGE_OPTIONS.map((o) => o.value));
export const VALID_COUNTRIES = new Set(COUNTRY_OPTIONS.map((o) => o.value));

export const normalizeValue = (inputValue, validSet) => {
  if (inputValue === null || inputValue === undefined) return null;

  // Convert to lowercase for case-insensitive matching
  const normalized = String(inputValue).toLowerCase().trim();
  if (!normalized || normalized === "null" || normalized === "none") return null;

  // Check if already in valid format
  if (validSet.has(normalized)) {
    return normalized;
  }

  // Try common transformations (dashes to underscores, spaces to underscores)
  const transformed = normalized.replace(/[-\s]/g, "_");
  if (validSet.has(transformed)) {
    return transformed;
  }
  if (transformed === "45_54" && validSet.has("45_55")) {
    return "45_55";
  }

  return null; // Invalid value
};

/**
 * Normalize age value from various formats (number, string, hyphenated, DB format)
 * Returns a valid age key from AGE_OPTIONS, or null if empty/null/invalid.
 */
export const normalizeAge = (inputValue) => {
  if (inputValue === null || inputValue === undefined) return null;
  const s = String(inputValue).trim().toLowerCase();
  if (!s || s === "null" || s === "none") return null;

  // Check if numeric age string (e.g. "27")
  const num = parseInt(s, 10);
  if (!isNaN(num) && String(num) === s) {
    if (num < 18) return "under_18";
    if (num <= 24) return "18_24";
    if (num <= 29) return "25_29";
    if (num <= 34) return "30_34";
    if (num <= 44) return "35_44";
    if (num <= 55) return "45_55";
    return "55_plus";
  }

  // Handle hyphen/underscore/spaces
  const transformed = s.replace(/[-\s]/g, "_");
  if (transformed === "45_54" || transformed === "45_55") return "45_55";
  if (transformed === "55_plus" || transformed === "55_or_older" || transformed === "55+") return "55_plus";
  if (transformed === "under_18" || transformed === "under18") return "under_18";
  if (VALID_AGES.has(transformed)) return transformed;
  if (VALID_AGES.has(s)) return s;

  return null;
};

/**
 * Normalize gender value from various formats (case variants, hyphens, DB format)
 * Returns a valid gender key from GENDER_OPTIONS, or null if empty/null/invalid.
 */
export const normalizeGender = (inputValue) => {
  if (inputValue === null || inputValue === undefined) return null;
  const s = String(inputValue).trim().toLowerCase();
  if (!s || s === "null" || s === "none") return null;

  const transformed = s.replace(/[-\s]/g, "_");
  if (VALID_GENDERS.has(transformed)) return transformed;
  if (VALID_GENDERS.has(s)) return s;

  return null;
};

/**
 * Get display label for a database value
 * @param {string} value - Database value
 * @param {Array} options - Array of {label, value} objects
 * @returns {string|null} - Display label or null if not found
 */
export const getDisplayLabel = (value, options) => {
  const option = options.find((o) => o.value === value);
  return option ? option.label : null;
};
